import React, { useEffect, useState } from 'react';
import { arrearsService, tenantPaymentService } from '../../services/api';

interface PaymentConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    tenantId: string;
    tenantName: string;
    rentAmount?: number;
    onPaymentRecorded?: () => void;
    onSendMessage?: (templateId: string) => void;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string; icon: string }> = {
    ON_TIME:    { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'ON TIME',    icon: 'fa-check-circle' },
    LATE:       { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'LATE',       icon: 'fa-clock' },
    PAID_LATE:  { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'PAID LATE',  icon: 'fa-clock' },
    PARTIAL:    { bg: 'bg-yellow-50',  text: 'text-yellow-700',  label: 'PARTIAL',    icon: 'fa-coins' },
    UPCOMING:   { bg: 'bg-blue-50',    text: 'text-blue-700',    label: 'UPCOMING',   icon: 'fa-bell' },
    SCHEDULED:  { bg: 'bg-slate-50',   text: 'text-slate-600',   label: 'SCHEDULED',  icon: 'fa-calendar' },
    OVERDUE:    { bg: 'bg-orange-50',  text: 'text-orange-700',  label: 'OVERDUE',    icon: 'fa-exclamation-circle' },
    CRITICAL:   { bg: 'bg-rose-50',    text: 'text-rose-700',    label: 'CRITICAL',   icon: 'fa-times-circle' },
    UNKNOWN:    { bg: 'bg-gray-50',    text: 'text-gray-600',    label: 'UNKNOWN',    icon: 'fa-question-circle' }
};

const formatYM = (ym: string) => {
    const [y, m] = ym.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const formatDate = (iso?: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const PaymentConfirmationModal: React.FC<PaymentConfirmationModalProps> = ({
    isOpen, onClose, tenantId, tenantName, rentAmount, onPaymentRecorded, onSendMessage
}) => {
    const [detail, setDetail] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [recordingFor, setRecordingFor] = useState<string | null>(null);
    const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [paymentAmount, setPaymentAmount] = useState<string>(rentAmount ? String(rentAmount) : '');
    const [paymentMethod, setPaymentMethod] = useState<string>('bank_transfer');
    const [paymentReference, setPaymentReference] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);
    const [portalCopied, setPortalCopied] = useState(false);
    // Portal preview modal — opens before the link is sent so the officer
    // can verify the URL + recipient before any email leaves the system.
    const [portalPreview, setPortalPreview] = useState<{ url: string; email?: string } | null>(null);
    const [portalSending, setPortalSending] = useState(false);

    const handleSendPortalLink = async () => {
        setPortalLoading(true);
        setPortalCopied(false);
        try {
            const res = await tenantPaymentService.generateLink(tenantId);
            const url = res.data?.data?.portalUrl;
            const email = res.data?.data?.tenantEmail || detail?.tenant?.email;
            if (url) {
                setPortalPreview({ url, email });
            }
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to generate portal link');
        } finally {
            setPortalLoading(false);
        }
    };

    const handleCopyPortalUrl = async () => {
        if (!portalPreview?.url) return;
        await navigator.clipboard.writeText(portalPreview.url);
        setPortalCopied(true);
        setTimeout(() => setPortalCopied(false), 3000);
    };

    const handleEmailPortalLink = async () => {
        if (!portalPreview?.url) return;
        setPortalSending(true);
        try {
            const res = await tenantPaymentService.sendLink(tenantId, portalPreview.url);
            const data = res.data?.data || {};
            // Show provider + messageId so any "sent but not delivered" issue
            // can be traced (e.g. SMTP says delivered but mail lands in spam).
            const providerHint = data.provider ? ` via ${data.provider}` : '';
            const idHint = data.messageId ? ` (id: ${data.messageId})` : '';
            alert(`Portal link sent to ${data.sentTo || portalPreview.email || tenantName}${providerHint}${idHint}\n\nIf the tenant doesn't receive it within a few minutes, check their spam folder.`);
            setPortalPreview(null);
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to send portal link');
        } finally {
            setPortalSending(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        const fetch = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await arrearsService.getTenantDetail(tenantId);
                setDetail(res.data?.data || null);
            } catch (err: any) {
                setError(err.response?.data?.message || err.message || 'Failed to load rent schedule');
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [isOpen, tenantId]);

    if (!isOpen) return null;

    const handleRecord = async (periodYM: string) => {
        if (!paymentAmount || isNaN(parseFloat(paymentAmount))) {
            alert('Please enter a valid amount');
            return;
        }
        setSubmitting(true);
        try {
            await arrearsService.recordPayment(tenantId, {
                periodYM,
                paymentDate,
                amount: parseFloat(paymentAmount),
                method: paymentMethod,
                reference: paymentReference
            });
            // Refresh
            const res = await arrearsService.getTenantDetail(tenantId);
            setDetail(res.data?.data || null);
            setRecordingFor(null);
            setPaymentReference('');
            onPaymentRecorded?.();
        } catch (err: any) {
            alert(err.response?.data?.message || err.message || 'Failed to record payment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (paymentId: string, periodYM: string) => {
        if (!window.confirm('Delete this payment record?')) return;
        try {
            await arrearsService.deletePayment(tenantId, paymentId, periodYM);
            const res = await arrearsService.getTenantDetail(tenantId);
            setDetail(res.data?.data || null);
            onPaymentRecorded?.();
        } catch (err: any) {
            alert(err.response?.data?.message || err.message || 'Failed to delete payment');
        }
    };

    const handleAcknowledge = async (periodYM: string) => {
        try {
            await arrearsService.acknowledgeAlert(tenantId, periodYM);
            const res = await arrearsService.getTenantDetail(tenantId);
            setDetail(res.data?.data || null);
        } catch (err: any) {
            alert(err.response?.data?.message || err.message || 'Failed to acknowledge alert');
        }
    };

    // Schedule rows in chronological order
    const schedule = (detail?.schedule || []).slice().sort((a: any, b: any) =>
        a.periodYM.localeCompare(b.periodYM)
    );
    const recommendation = detail?.recommendation;
    const summary = detail?.summary || {};

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-black text-gray-900 uppercase tracking-tight">
                            <i className="fas fa-money-check-alt text-emerald-500 mr-2"></i>
                            Rent Schedule — {tenantName}
                        </h2>
                        {detail && (
                            <p className="text-xs text-gray-500 mt-1">
                                Status: <span className="font-bold">{detail.status}</span>
                                {summary.arrears > 0 && (
                                    <> · Arrears: <span className="font-bold text-rose-600">£{summary.arrears.toFixed(2)}</span></>
                                )}
                                {summary.missedMonths > 0 && (
                                    <> · Missed: <span className="font-bold text-rose-600">{summary.missedMonths} month(s)</span></>
                                )}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-700 text-2xl leading-none w-8 h-8 flex items-center justify-center"
                    >
                        &times;
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading && (
                        <div className="py-12 text-center">
                            <div className="animate-pulse flex flex-col items-center">
                                <div className="h-10 w-10 bg-emerald-200 rounded-full mb-3"></div>
                                <div className="h-3 w-32 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="py-12 text-center">
                            <i className="fas fa-exclamation-triangle text-amber-500 text-3xl mb-3"></i>
                            <p className="text-sm text-gray-700">{error}</p>
                        </div>
                    )}

                    {!loading && !error && detail && (
                        <>
                            {/* RRA Recommendation Banner */}
                            {recommendation && recommendation.severity !== 'INFO' && (
                                <div className={`mb-4 p-4 rounded-xl border-2 ${
                                    recommendation.severity === 'CRITICAL' ? 'bg-rose-50 border-rose-300' :
                                    recommendation.severity === 'HIGH' ? 'bg-orange-50 border-orange-300' :
                                    'bg-amber-50 border-amber-300'
                                }`}>
                                    <div className="flex items-start gap-3">
                                        <i className={`fas ${
                                            recommendation.severity === 'CRITICAL' ? 'fa-gavel text-rose-600' :
                                            recommendation.severity === 'HIGH' ? 'fa-exclamation-triangle text-orange-600' :
                                            'fa-bell text-amber-600'
                                        } text-xl mt-0.5`}></i>
                                        <div className="flex-1">
                                            <h3 className="text-sm font-black uppercase tracking-tight text-gray-900">
                                                {recommendation.title}
                                            </h3>
                                            <p className="text-xs text-gray-700 mt-1">
                                                {recommendation.detail}
                                            </p>
                                            {recommendation.nextSteps && recommendation.nextSteps.length > 0 && (
                                                <ul className="mt-2 space-y-1">
                                                    {recommendation.nextSteps.map((step: string, i: number) => (
                                                        <li key={i} className="text-xs text-gray-700 flex items-start gap-2">
                                                            <i className="fas fa-chevron-right text-[8px] mt-1 opacity-50"></i>
                                                            <span>{step}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            {recommendation.suggestedTemplate && onSendMessage && (
                                                <button
                                                    onClick={() => onSendMessage(recommendation.suggestedTemplate)}
                                                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-slate-800"
                                                >
                                                    <i className="fas fa-paper-plane"></i>
                                                    Send Recommended Notice
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Schedule Table */}
                            <div className="space-y-1.5">
                                {schedule.length === 0 && (
                                    <p className="text-sm text-gray-500 text-center py-8">No rent schedule available</p>
                                )}
                                {schedule.slice().reverse().map((row: any) => {
                                    const style = STATUS_STYLES[row.status] || STATUS_STYLES.UNKNOWN;
                                    const isRecording = recordingFor === row.periodYM;
                                    const hasPayment = row.payments && row.payments.length > 0;
                                    return (
                                        <div key={row.periodYM} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                            <div className="px-4 py-3 flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg ${style.bg} ${style.text} flex items-center justify-center`}>
                                                    <i className={`fas ${style.icon} text-sm`}></i>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-black text-gray-900 uppercase tracking-tight">
                                                        {formatYM(row.periodYM)}
                                                    </div>
                                                    <div className="text-[10px] text-gray-500">
                                                        Due: {formatDate(row.dueDate)}
                                                        {row.daysOverdue !== null && row.daysOverdue > 0 && (
                                                            <> · <span className="text-rose-600 font-bold">{row.daysOverdue} day(s) overdue</span></>
                                                        )}
                                                        {row.daysUntilDue !== null && row.daysUntilDue > 0 && row.status === 'UPCOMING' && (
                                                            <> · <span className="text-blue-600 font-bold">Due in {row.daysUntilDue} day(s)</span></>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-sm font-black text-gray-900">
                                                        £{(row.paidAmount || 0).toFixed(2)} <span className="text-gray-400 text-xs">/ £{row.amount.toFixed(2)}</span>
                                                    </div>
                                                    {row.balance > 0 && (
                                                        <div className="text-[10px] font-bold text-yellow-700">
                                                            Balance £{row.balance.toFixed(2)}
                                                        </div>
                                                    )}
                                                    <div className={`text-[9px] font-black uppercase ${style.text}`}>
                                                        {style.label}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {/* Allow Add for any month that still has a balance —
                                                        including PARTIAL (instalment top-up). Status alone
                                                        isn't enough: a payment recorded a few days after the
                                                        due date is ALSO tagged 'LATE' by computeMonthStatus
                                                        even though it's fully paid (balance=0) — require an
                                                        actual outstanding balance so the button doesn't show
                                                        for a month that's already settled. */}
                                                    {(row.status === 'OVERDUE' || row.status === 'CRITICAL' || row.status === 'LATE' || row.status === 'UPCOMING' || row.status === 'PARTIAL') && (row.balance ?? 0) > 0 && (
                                                        <button
                                                            onClick={() => {
                                                                setRecordingFor(isRecording ? null : row.periodYM);
                                                                // Pre-fill the input with the outstanding
                                                                // balance, not the full month rent, so the
                                                                // common "second instalment" case is one click.
                                                                const prefill = row.balance > 0 ? row.balance : row.amount;
                                                                setPaymentAmount(String(prefill));
                                                            }}
                                                            className="px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-emerald-700"
                                                        >
                                                            {hasPayment ? 'Add Instalment' : 'Record Payment'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {hasPayment && (
                                                <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 space-y-1">
                                                    {row.payments.map((p: any) => (
                                                        <div key={p.id} className="flex items-center justify-between text-[11px]">
                                                            <div className="flex items-center gap-2">
                                                                <i className="fas fa-check-circle text-emerald-500"></i>
                                                                <span className="font-medium text-gray-700">
                                                                    £{parseFloat(p.amount).toFixed(2)} on {formatDate(p.paymentDate)}
                                                                </span>
                                                                <span className="text-gray-400">via {p.method || 'manual'}</span>
                                                                {p.reference && <span className="text-gray-400">· ref: {p.reference}</span>}
                                                            </div>
                                                            <button
                                                                onClick={() => handleDelete(p.id, row.periodYM)}
                                                                className="text-rose-500 hover:text-rose-700 text-[10px] font-bold"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {isRecording && (
                                                <div className="px-4 py-3 bg-emerald-50 border-t border-emerald-100">
                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                                        <div>
                                                            <label className="text-[9px] font-black text-gray-500 uppercase">Date Paid</label>
                                                            <input
                                                                type="date"
                                                                value={paymentDate}
                                                                onChange={(e) => setPaymentDate(e.target.value)}
                                                                className="w-full mt-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-[9px] font-black text-gray-500 uppercase">Amount (£)</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={paymentAmount}
                                                                onChange={(e) => setPaymentAmount(e.target.value)}
                                                                className="w-full mt-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-[9px] font-black text-gray-500 uppercase">Method</label>
                                                            <select
                                                                value={paymentMethod}
                                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                                                className="w-full mt-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                                                            >
                                                                <option value="bank_transfer">Bank Transfer</option>
                                                                <option value="standing_order">Standing Order</option>
                                                                <option value="cash">Cash</option>
                                                                <option value="cheque">Cheque</option>
                                                                <option value="card">Card</option>
                                                                <option value="dd">Direct Debit</option>
                                                                <option value="other">Other</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="text-[9px] font-black text-gray-500 uppercase">Reference</label>
                                                            <input
                                                                type="text"
                                                                value={paymentReference}
                                                                onChange={(e) => setPaymentReference(e.target.value)}
                                                                placeholder="Optional"
                                                                className="w-full mt-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2 mt-3">
                                                        <button
                                                            onClick={() => handleRecord(row.periodYM)}
                                                            disabled={submitting}
                                                            className="flex-1 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-black uppercase tracking-tighter hover:bg-emerald-700 disabled:opacity-50"
                                                        >
                                                            {submitting ? 'Saving…' : 'Confirm Payment'}
                                                        </button>
                                                        <button
                                                            onClick={() => setRecordingFor(null)}
                                                            className="px-3 py-2 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                    <button
                        onClick={handleSendPortalLink}
                        disabled={portalLoading}
                        className="px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-emerald-50 disabled:opacity-50"
                    >
                        {portalLoading ? 'Generating…' : portalCopied ? 'Link Copied!' : 'Send Portal Link'}
                    </button>
                    <div className="flex items-center gap-3">
                        <p className="text-[10px] text-gray-500 hidden sm:block">
                            Status: 1 wk before → notify · 1d late LATE · 1wk OVERDUE · 2wk CRITICAL
                        </p>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-black uppercase tracking-tighter hover:bg-slate-800"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* Portal Link Preview — opens before any email is sent.
                Officer reviews the URL + recipient, then chooses to copy,
                open in a new tab, or send via email. */}
            {portalPreview && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setPortalPreview(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 border border-gray-200" onClick={e => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                                    <i className="fas fa-link text-emerald-600 mr-2"></i>
                                    Portal Link Preview
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">Review before sending to {tenantName}</p>
                            </div>
                            <button onClick={() => setPortalPreview(null)} className="text-gray-400 hover:text-gray-600">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="space-y-3 mb-5">
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Recipient</label>
                                <p className="text-sm text-gray-900 font-medium mt-0.5">{tenantName}{portalPreview.email ? ` · ${portalPreview.email}` : ''}</p>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Portal URL</label>
                                <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-xs text-gray-700 break-all">
                                    {portalPreview.url}
                                </div>
                            </div>
                            <div className="text-[11px] text-gray-500 italic">
                                <i className="fas fa-info-circle mr-1"></i>
                                The tenant will see their rent schedule, latest receipts and a secure payment-reference (LP-XXXX-NN) lookup.
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2 justify-end">
                            <button
                                onClick={() => window.open(portalPreview.url, '_blank', 'noopener,noreferrer')}
                                className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-50"
                            >
                                <i className="fas fa-external-link-alt mr-1.5"></i>
                                Open Preview
                            </button>
                            <button
                                onClick={handleCopyPortalUrl}
                                className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-50"
                            >
                                <i className="fas fa-copy mr-1.5"></i>
                                {portalCopied ? 'Copied!' : 'Copy Link'}
                            </button>
                            <button
                                onClick={handleEmailPortalLink}
                                disabled={portalSending}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
                            >
                                <i className="fas fa-paper-plane mr-1.5"></i>
                                {portalSending ? 'Sending…' : 'Send to Tenant'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentConfirmationModal;
